import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiClient } from '../../api/apiContext';
import { queryKeys } from '../../api/hooks';
import type { StatementResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { percent } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { compact, count, pounds } from '../../i18n/format';
import { magnitude } from '../catalog/money';

/** Months in the trend, the chosen one last. */
const MONTHS = 6;
/** Past this many, "what was delivered" shows the rest behind "Show all". */
const FIRST_SERVICES = 8;

type Measure = 'delivered' | 'owed' | 'sessions' | 'share';

interface Period {
  readonly year: number;
  readonly month: number;
}

/**
 * The doctor statements screen's charts (CR-35), from the statements the screen already loads.
 *
 * The question this screen is opened for is "what do we owe, and is it reasonable": so the strip
 * puts what the doctors delivered beside what they are owed and the share one is of the other, and
 * the doctor chart compares them on any of the four measures, pay as a share of delivered value
 * being the one that says whether an arrangement is costing more than it brings. The month's
 * sessions day by day, what was delivered, and six months of the same totals sit under it.
 *
 * Owed counts only doctors whose pay is set: a doctor with none has no figure (the table says "not
 * set"), and adding a zero for them would understate what the clinic owes. The strip says how many
 * that is, in the alert ink, because it is the one thing here someone has to do.
 *
 * A doctor sees only their own statement (the server's rule), so for them the doctor chart, which
 * would have one bar, is left out.
 */
export function StatementsOverview({
  clinicId,
  period,
  statements,
  today,
}: {
  readonly clinicId: string;
  readonly period: Period;
  readonly statements: readonly StatementResponse[];
  /** The clinic's today, to mark it in the chosen month's days. */
  readonly today: string;
}) {
  const { t, i18n } = useTranslation();
  const client = useApiClient();
  const [measure, setMeasure] = useState<Measure>('delivered');
  const [trendMeasure, setTrendMeasure] = useState<'delivered' | 'owed'>('delivered');

  const set = statements.filter((statement) => statement.model && statement.model !== 'UNSET');
  const delivered = sum(statements.map((statement) => magnitude(statement.deliveredValue)));
  const owed = sum(set.map((statement) => magnitude(statement.computedTotal)));
  const sessions = sum(statements.map((statement) => statement.sessionsCompleted ?? 0));
  const unset = statements.length - set.length;
  const issued = statements.filter((statement) => statement.issuedAt).length;

  // The five months before the chosen one, fetched beside it; the chosen month is the screen's own
  // query, so it comes from the cache rather than a second request.
  const months = Array.from({ length: MONTHS }, (_, index) => shiftMonth(period, index - (MONTHS - 1)));
  const trend = useQueries({
    queries: months.map((month) => {
      const key = monthKey(month);
      return {
        queryKey: queryKeys.statements(clinicId, key),
        // Fresh for a minute: the chosen month was fetched by the screen a moment ago, and
        // without this it would be fetched again the instant the charts appear.
        staleTime: 60_000,
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          client.get<StatementResponse[]>(`/clinics/${clinicId}/statements?year=${month.year}&month=${month.month}`, {
            signal,
          }),
      };
    }),
  });

  const doctorRows: RankedBar[] = statements
    .map((statement) => {
      const value = magnitude(statement.deliveredValue);
      const pay = statement.model && statement.model !== 'UNSET' ? magnitude(statement.computedTotal) : null;
      const figure: Record<Measure, number | null> = {
        delivered: value,
        owed: pay,
        sessions: statement.sessionsCompleted ?? 0,
        share: pay !== null && value > 0 ? (pay / value) * 100 : null,
      };
      return { key: statement.employmentId ?? '', label: statement.doctorName ?? '—', value: figure[measure] };
    })
    .filter((row): row is RankedBar => row.value !== null)
    .sort((a, b) => b.value - a.value);
  const formatMeasure: Record<Measure, (value: number) => string> = {
    delivered: pounds,
    owed: pounds,
    sessions: count,
    share: (value) => `${Math.round(value)}%`,
  };

  const lines = statements.flatMap((statement) => statement.lines ?? []);
  const dayColumns = daysOf(period, today).map((day) => {
    const onDay = lines.filter((line) => line.occurredOn === day);
    const span = { start: day, end: day };
    return {
      key: day,
      label: bucketLabel(span, 'day', i18n.language),
      description: t('overview.statements.dayDescription', {
        when: bucketTitle(span, 'day', i18n.language, (first, last) => t('overview.weekOf', { from: first, to: last })),
        count: onDay.length,
        delivered: pounds(sum(onDay.map((line) => magnitude(line.deliveredValue)))),
      }),
      segments: [{ value: onDay.length, colour: 'var(--chart-1)' }],
    };
  });

  const services = new Map<string, RankedBar>();
  for (const line of lines) {
    const label = line.label ?? '—';
    const running = services.get(label);
    services.set(label, { key: label, label, value: (running?.value ?? 0) + magnitude(line.deliveredValue) });
  }
  const serviceRows = [...services.values()].filter((row) => row.value > 0).sort((a, b) => b.value - a.value);

  const trendColumns: Column[] = months.map((month, index) => {
    const rows = trend[index]?.data ?? [];
    const monthDelivered = sum(rows.map((row) => magnitude(row.deliveredValue)));
    const monthOwed = sum(
      rows.filter((row) => row.model && row.model !== 'UNSET').map((row) => magnitude(row.computedTotal)),
    );
    const start = `${monthKey(month)}-01`;
    return {
      key: start,
      label: bucketLabel({ start, end: start }, 'month', i18n.language),
      description: t('overview.statements.monthDescription', {
        month: bucketTitle({ start, end: start }, 'month', i18n.language, (first) => first),
        delivered: pounds(monthDelivered),
        owed: pounds(monthOwed),
        sessions: sum(rows.map((row) => row.sessionsCompleted ?? 0)),
      }),
      segments: [
        {
          value: trendMeasure === 'delivered' ? monthDelivered : monthOwed,
          colour: trendMeasure === 'delivered' ? 'var(--chart-1)' : 'var(--chart-2)',
        },
      ],
    };
  });

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.statements.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.statements.delivered', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(delivered))}</span>
          <span className="measure__note">{t('overview.statements.sessions', { count: sessions })}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.statements.owed', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(owed))}</span>
          <span className="measure__note">
            {delivered > 0
              ? t('overview.statements.owedShare', { share: percent(owed, delivered) })
              : t('overview.statements.nothingDelivered')}
          </span>
        </div>
        <div className={unset > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.statements.doctors')}</span>
          <span className="measure__value">{count(statements.length)}</span>
          <span className="measure__note">
            {unset > 0 ? t('overview.statements.unset', { count: unset }) : t('overview.statements.allSet')}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.statements.issued')}</span>
          <span className="measure__value">{count(issued)}</span>
          <span className="measure__note">{t('overview.statements.issuedNote', { total: statements.length })}</span>
        </div>
      </section>

      <div className="overview-grid">
        {statements.length > 1 ? (
          <section className="panel">
            <div className="panel__header">
              <div className="panel__heading">
                <h2>{t('overview.statements.byDoctor')}</h2>
                <p className="muted">{t(`overview.statements.measureNote.${measure}`)}</p>
              </div>
              <div className="tabs" role="tablist" aria-label={t('overview.statements.byDoctor')}>
                {(['delivered', 'owed', 'sessions', 'share'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="tab"
                    aria-selected={measure === option}
                    className={measure === option ? 'tab tab--active' : 'tab'}
                    onClick={() => setMeasure(option)}
                  >
                    {t(`overview.statements.view.${option}`)}
                  </button>
                ))}
              </div>
            </div>
            {doctorRows.length > 0 ? (
              <RankedBars rows={doctorRows} format={formatMeasure[measure]} share={measure !== 'share'} />
            ) : (
              <p className="muted">{t('overview.statements.nothingToCompare')}</p>
            )}
          </section>
        ) : null}

        {dayColumns.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.statements.perDay')}</h2>
              <p className="muted">{t('overview.statements.perDayNote')}</p>
            </div>
            <Columns columns={dayColumns} label={t('overview.statements.perDay')} current={today} />
          </section>
        ) : null}

        {serviceRows.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.statements.byService')}</h2>
              <p className="muted">{t('overview.statements.byServiceNote')}</p>
            </div>
            <RankedBars rows={serviceRows} format={pounds} firstRows={FIRST_SERVICES} />
          </section>
        ) : null}

        <section className="panel">
          <div className="panel__header">
            <div className="panel__heading">
              <h2>{t('overview.statements.sixMonths')}</h2>
              <p className="muted">{t('overview.statements.sixMonthsNote')}</p>
            </div>
            <div className="tabs" role="tablist" aria-label={t('overview.statements.sixMonths')}>
              {(['delivered', 'owed'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={trendMeasure === option}
                  className={trendMeasure === option ? 'tab tab--active' : 'tab'}
                  onClick={() => setTrendMeasure(option)}
                >
                  {t(`overview.statements.view.${option}`)}
                </button>
              ))}
            </div>
          </div>
          <Columns
            columns={trendColumns}
            label={t('overview.statements.sixMonths')}
            formatTick={compact}
            current={`${monthKey(period)}-01`}
          />
        </section>
      </div>
    </div>
  );
}

function sum(values: readonly number[]): number {
  return values.reduce((running, value) => running + value, 0);
}

function monthKey({ year, month }: Period): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function shiftMonth({ year, month }: Period, by: number): Period {
  const index = year * 12 + (month - 1) + by;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** Every day of the month, or of it so far when it is the month now running. */
function daysOf(period: Period, today: string): string[] {
  const last = new Date(Date.UTC(period.year, period.month, 0)).getUTCDate();
  const days: string[] = [];
  for (let day = 1; day <= last; day++) {
    const date = `${monthKey(period)}-${String(day).padStart(2, '0')}`;
    if (date > today) {
      break;
    }
    days.push(date);
  }
  return days;
}
